"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const B = ({ children }) => (
  <span className="geist-medium text-[#111827]">{children}</span>
);

const COLUMNS = [
  { col: "Item#", desc: "Item number from Item Master" },
  { col: "Item Status", desc: "Active or Inactive on the item" },
  { col: "Item Name", desc: "Name from Item Master" },
  { col: "Item Type", desc: "Type from Item Master" },
  { col: "Model No", desc: "Model number when set" },
  { col: "Group", desc: "Item Group" },
  {
    col: "Total Available Qty",
    desc: "On-hand quantity. Company total, or the sum in the warehouses you filtered",
  },
  {
    col: "Total Valuation",
    desc: "Stored inventory value for that same scope — not a live qty × average on this screen",
  },
  {
    col: "Minimum / Maximum Stock Level",
    desc: "From Item Master. Used by the stock-status tiles and the Stock Status column",
  },
  {
    col: "Stock Status",
    desc: "Low Stock, Overstocked, or In Stock from min/max vs available qty",
  },
  {
    col: "Stock Variance",
    desc: "How far below min (Low Stock) or above max (Overstocked). Blank when In Stock",
  },
  {
    col: "Added By / Created At / Updated By / Updated At",
    desc: "Who last touched the item inventory record",
  },
];

const RULES = [
  {
    topic: "This is a stock position, not a period report",
    rule: "There is no date range, Opening Qty, In, or Out on this list. Those movements live on Reports → Item Ledger and on the item's batch drawer",
  },
  {
    topic: "Tracked inventory items only",
    rule: "This list is Track inventory goods. Service items never get a stock record and never appear. Items with Track inventory off do not show — they do not hold stock",
  },
  {
    topic: "Zero stock is off by default",
    rule: "Include out-of-stock items must be on to see rows whose available qty is 0. Header Total Count / Total Qty / Total Value follow the current filters",
  },
  {
    topic: "Min / max tiles",
    rule: "In Range, Below Min, Above Max, No Range. No Range = both min and max are 0 or empty. Click a tile to filter. Click again to clear. Bounds are Item Master minimum / maximum stock level",
  },
  {
    topic: "Valuation",
    rule: "Total Valuation is the stored inventory value (qty × purchase price on each batch, FIFO on deduct). Inventory Sales Method on the item is FIFO. LIFO is labelled Coming soon and disabled",
  },
  {
    topic: "When the number changes",
    rule: "Available qty moves when a document actually posts stock: Invoice or Delivery Challan per Inventory Config, GRPO or Bill per Inventory Config, production, Inventory Transfer Approve, Inventory Adjustment Approve. A Sales Order never takes stock. A Pending transfer or Draft adjustment does not",
  },
  {
    topic: "View Ledger",
    rule: "Opens Reports → Item Ledger for that item. It is not Accountant → Account Ledger",
  },
  {
    topic: "No Add, Edit, or Delete here",
    rule: "This screen is view + export. Create items on Item Master. Move stock on Transfer or Adjustment",
  },
];

const sections = [
  {
    heading: "What this module does",
    subsections: [
      { heading: "1. The list, totals, and stock-status tiles" },
      { heading: "2. Search, filters, and export" },
      { heading: "3. Item detail — overview, pipeline, warehouses" },
      { heading: "4. Batches and deductions" },
      { heading: "5. View Ledger" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p
        key={item.term}
        className="text-[16px] geist-regular text-[#374151] leading-relaxed"
      >
        <span className="geist-medium text-[#111827]">{item.term}:</span>{" "}
        {item.desc}
      </p>
    ))}
  </div>
);

export default function ViewInventoryPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            View Inventory
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Anyone who needs on-hand qty and value by item — not a
            transfer, not an adjustment
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            <B>View Inventory</B> is the live stock position. Each row is one
            item: available quantity, stored value, min/max status. Click a row
            for warehouses, batches, and the pipeline (incoming vs committed).
            It does not create stock. Sales, purchase, production, transfers,
            and adjustments do — when those documents actually post.
          </p>
          <Callout type="note">
            Only items with <B>Track inventory</B> on show here. <B>Service</B>{" "}
            items and goods with Track inventory off do not.
          </Callout>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this module does
          </h2>

          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. The list, totals, and stock-status tiles
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Title View Inventory. Header stats: <B>Total Count</B>,{" "}
            <B>Total Value</B>, <B>Total Qty</B>. Tiles: <B>In Range</B>,{" "}
            <B>Below Min</B>, <B>Above Max</B>, <B>No Range</B> with counts.
            Export (xlsx). Toolbar for column picker, refresh, reset filters,
            and Inventory Analytics (needs Inventory Analytics view).
          </p>
          <Callout type="note">
            Rows are <B>tracked inventory</B> items. A Service item or a goods
            item with Track inventory off is not on this list.
          </Callout>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white mb-4 overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Column
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    What it shows
                  </th>
                </tr>
              </thead>
              <tbody>
                {COLUMNS.map((c, i) => (
                  <tr
                    key={c.col}
                    className={
                      i < COLUMNS.length - 1 ? "border-b border-[#F5F5F5]" : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top whitespace-nowrap">
                      {c.col}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {c.desc}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            Action: <B>View Stock Details</B> (same as row click) and{" "}
            <B>View ledger</B>.
          </p>
          <Screenshot caption="Screenshot: View Inventory list with Total Count, Total Qty, Total Value, and In Range / Below Min tiles." />

          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Search, filters, and export
          </h3>
          <Definitions
            items={[
              {
                term: "Search",
                desc: (
                  <>
                    Matches item name, item type, item number, or model number.
                  </>
                ),
              },
              {
                term: "Include out-of-stock items",
                desc: (
                  <>
                    Switch. Off by default — rows with available qty 0 are
                    hidden.
                  </>
                ),
              },
              {
                term: "What (filter drawer)",
                desc: (
                  <>
                    Item Status (Active / Inactive), Item Type, Item Group,
                    Item.
                  </>
                ),
              },
              {
                term: "Where",
                desc: (
                  <>
                    Division, then Branch, then Warehouse. Qty and value then
                    show only those warehouses.
                  </>
                ),
              },
              {
                term: "Export",
                desc: (
                  <>
                    <B>Export Inventory Stock</B> — xlsx only. Columns: Item ID,
                    Item Name, Item Type, Model No, Item Group, Total Available
                    Qty, Total Valuation. Uses the same filters, including
                    out-of-stock.
                  </>
                ),
              },
            ]}
          />
          <Callout type="note">
            There is no date range and no category filter on this list. Item
            Group is the closest grouping.
          </Callout>
          <Screenshot caption="Screenshot: Include out-of-stock items switch and the filter drawer (What / Where)." />

          <h3
            id="section-0-sub-2"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            3. Item detail — overview, pipeline, warehouses
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Row click → item name in the header, Item#, Active/Inactive. Stats:{" "}
            <B>Total Stock</B>, <B>Inventory Value</B>. <B>View Ledger</B> on
            the right.
          </p>
          <Definitions
            items={[
              {
                term: "Item Summary",
                desc: <>Item No, Item Name, Item Type, Model No, Item Group.</>,
              },
              {
                term: "Inventory Summary",
                desc: (
                  <>
                    Total Opening Stock, Total Available Stock, Inventory Sales
                    Method (FIFO), Total Inventory Value.
                  </>
                ),
              },
              {
                term: "Inventory Pipeline",
                desc: (
                  <>
                    Available now + Incoming − Committed = Projected. Incoming:
                    purchase orders (Accepted / Partially Recorded, not yet
                    received or billed) plus GRPOs Recorded that have not added
                    stock. Committed: quotes Sent/Accepted, sales orders
                    Open/Partially Recorded less delivered or invoiced, and
                    standalone delivery challans Sent that have not deducted
                    yet. Draft documents are shown but excluded from totals.
                  </>
                ),
              },
              {
                term: "Warehouses",
                desc: (
                  <>
                    Division, Branch, Warehouse, Opening Stock, Available Stock,
                    Valuation.
                  </>
                ),
              },
            ]}
          />
          <Callout type="warning">
            Projected can go negative (Short by …). That is committed demand vs
            on-hand plus incoming — it is not a posted negative stock figure on
            the list.
          </Callout>
          <Screenshot caption="Screenshot: item detail with Inventory Pipeline (Available, Incoming, Committed, Projected) and the warehouses table." />

          <h3
            id="section-0-sub-3"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            4. Batches and deductions
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Batches table: Batch No, Division, Branch, Warehouse, Quantity,
            Remaining Qty, Purchase Price, Vendor / User batch numbers, Vendor
            Expiry, Document ID, Source Type. Click a batch →{" "}
            <B>Batch Details</B> drawer with Quantity, Remaining Qty, Purchase
            Price, and a <B>Deductions</B> table (Document No, Source Type,
            Quantity Deducted, Selling Price, Deducted At).
          </p>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            Source types you will see on batches include GRPO, Bill, Credit
            Note, production, opening stock, existing inventory, manual stock
            add, inventory transfer (manual or production), inventory
            adjustment, and rejection recovery. Document ID opens that document
            when a route exists.
          </p>
          <Screenshot caption="Screenshot: Batches table and Batch Details drawer with Deductions." />

          <h3
            id="section-0-sub-4"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            5. View Ledger
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            List action or detail button. Opens <B>Reports → Item Ledger</B>{" "}
            filtered to this item — the movement history (In / Out by document).
            That is not Chart of Accounts and not Accountant → Account Ledger.
          </p>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Topic
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((r, i) => (
                  <tr
                    key={r.topic}
                    className={
                      i < RULES.length - 1 ? "border-b border-[#F5F5F5]" : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[220px]">
                      {r.topic}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {r.rule}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            href="/documentation/books/inventory/inventory-transfer"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Inventory Transfer <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Move qty between warehouses on Approve — not on Save
            </p>
          </Link>
          <Link
            href="/documentation/books/inventory/inventory-adjustment"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Inventory Adjustment <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Correct the count; stock and ledger post on Approve
            </p>
          </Link>
          <Link
            href="/documentation/books/settings/inventory-config"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Inventory Config <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              When Invoice vs Delivery Challan deducts, and GRPO vs Bill adds
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
